<template>
  <li :class="{'bg-highlightBaseLight': !mention.read}">
    <div class="flex items-start border-b p-4">
      <lm-avatar class="flex-shrink-0 mr-3" :src="userAvatar" size="32px" />
      <div class="flex flex-col flex-grow">
        <div class="mb-2 leading-tight">
          <span class="text-gray-900 font-medium">{{ userName }}</span>
          <span v-if="numUsers > 1" class="text-gray-500">
            and {{ numUsers - 1 }}
            {{ 'other' | pluralizeWord(numUsers - 1) }}
          </span>
          <span class="text-gray-700" v-html="message" />
        </div>
        <span
          v-if="mention.type === MentionTypes.COMMENT && numUsers === 1"
          class="mb-3 text-s italic text-gray-500"
        >
          "{{ mention.excerpt }}"
        </span>
        <span
          v-if="mention.action !== MentionActions.INVITATION"
          class="text-s text-highlight cursor-pointer"
          @click="openMention"
          >{{ excerpt }}</span
        >
        <div class="flex justify-between items-center">
          <span class="text-xxs text-gray-500"
            >{{ mention.updatedAt | dateFromNow }} ago</span
          >
          <lm-icon-button
            class="DismissButton ml-3"
            icon="notInterested"
            tooltip="Dismiss"
            svg
            svg-size="18"
            @click="dismiss"
          />
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import {mapGetters} from 'vuex'
import LmAvatar from '@/components/ui/LmAvatar'
import LmIconButton from '@/components/ui/LmIconButton'
import {MentionTypes, MentionActions} from '@/lib/definitions'

export default {
  name: 'MentionItem',
  components: {LmAvatar, LmIconButton},
  props: {
    mention: {type: Object, required: true}
  },
  data() {
    return {MentionTypes, MentionActions}
  },
  computed: {
    ...mapGetters(['getUserByID', 'userId', 'getActorByID']),
    excerpt() {
      if (this.mention.type === MentionTypes.COMMENT)
        return this.mention.refExcerpt
      return this.mention.excerpt
    },
    userName() {
      // API-466 fixed invite mention
      if (
        this.mention.action === MentionActions.INVITATION &&
        this.mention.type === MentionTypes.REJECTED &&
        this.mention.excerpt
      ) {
        return this.mention.excerpt
      }
      const user = this.getUserByID(this.mention.from[0])
      return user ? user.username : ''
    },
    userAvatar() {
      const user = this.getUserByID(this.mention.from[0])
      return user ? user.picture : ''
    },
    numUsers() {
      return this.mention.from.length
    },
    message() {
      let message = ''
      const mention = this.mention
      switch (mention.action) {
        case MentionActions.LEFT:
          message = ' is no longer a member of this landscape'
          break
        case MentionActions.CREATED: {
          if (mention.type === MentionTypes.COMMENT) {
            message = ` commented on your ${mention.ref.toLowerCase()}`
          } else {
            message = ` created a new ${mention.type.toLowerCase()}`
          }
          break
        }
        case MentionActions.INVITATION: {
          const invitor = this.getUserByID(this.mention.ref)
          const text = !invitor._id
            ? 'an'
            : invitor._id !== this.userId
            ? `${invitor.username}'s`
            : 'your'
          if (mention.type === MentionTypes.ACCEPTED)
            message = ` accepted ${text} invitation and is now a member of this landscape`
          else message = ` rejected ${text} invitation to join this landscape`
          break
        }
        case MentionActions.ASSIGNED:
          message = ` assigned you an insight`
          break
        case MentionActions.MENTION: {
          let a = 'a'
          if (mention.type === MentionTypes.INSIGHT) a = 'an'
          message = ` mentioned you in ${a} ${mention.type.toLowerCase()}`
          break
        }
        case MentionActions.VOTED:
          message = ` voted +1 on your ${mention.type.toLowerCase()}`
          break
        case MentionActions.FLAGGED:
          message = ` flagged your ${mention.type.toLowerCase()}`
          break
        case MentionActions.ROLE:
          message = ` changed your user role to <span class="capitalize font-medium">${mention.type.toLowerCase()}</span>`
          break
        case MentionActions.PRIORITY:
          message = ` marked a ${mention.type.toLowerCase()} as  <span class="font-medium">Important</span>`
          break
        case MentionActions.SQUASH:
          {
            message = ' squashed an actor:'
          }
          break
        case MentionActions.MOVE:
          {
            message = ` moved the actor <span class="font-medium">${mention.ref}</span> into:`
          }
          break
        case MentionActions.MERGE:
          {
            message = ` merged the actor <span class="font-medium">${mention.ref}</span> into:`
          }
          break
        case MentionActions.UPDATED:
          {
            message = ` renamed the actor <span class="font-medium">${mention.ref}</span> to:`
          }
          break
        case MentionActions.DELETED:
          {
            message = ' deleted an actor:'
          }
          break
        default:
          message = ' - New action'
      }
      return message
    }
  },
  methods: {
    openMention() {
      let name
      let query = {}
      const mention = this.mention
      const params = {}
      switch (mention.type) {
        case MentionTypes.ACTOR:
          {
            name = 'info'
            const actor = this.getActorByID(mention.object)
            if (actor.pathArr) params.actor = actor.pathArr
          }
          break
        case MentionTypes.COMMENT:
          name = mention.ref.toLowerCase()
          if (name === 'topic') {
            query[name] = mention.refKey
          } else {
            params[`${name}Slug`] = mention.refKey
          }
          query['c'] = mention.object
          break
        case MentionTypes.TOPIC:
          name = mention.type.toLowerCase()
          query[name] = mention.object
          break
        case MentionTypes.INSIGHT:
        case MentionTypes.PREMISE:
          name = mention.type.toLowerCase()
          params[`${name}Slug`] = mention.refKey
          break
        default:
          console.error('Unhandled mention type:' + mention.type)
          return
      }
      if (name === 'topic') {
        this.$router.pushQuery(query)
      } else {
        this.$router.push({name, params, query}).catch(() => {})
      }
    },
    dismiss() {
      this.$store.dispatch('dismissMention', {key: this.mention.key})
    }
  }
}
</script>

<style>
.DismissButton svg {
  color: theme('colors.gray.300');
}
</style>
